import { useMutation } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { PhoneInput } from 'components/FormFields';
import Toaster from 'components/Toaster';

// GraphQL Mutation
const MOBILE_DELIVERY_MUTATION = gql`
  mutation mobileDelivery($input: MobileDeliveryInput!) {
    mobileDelivery(input: $input)
  }
`;

// Styled Components
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1e293b' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 12px;
  width: 540px;
  max-width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  margin: 0;
`;

const CloseButton = styled.button`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.05)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#6B7280')};
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  }
`;

const InfoBanner = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(59, 130, 246, 0.1)'
      : 'rgba(59, 130, 246, 0.05)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(59, 130, 246, 0.2)'
        : 'rgba(59, 130, 246, 0.15)'};
  border-radius: 8px;
  padding: 12px;
  display: flex;
  gap: 12px;
`;

const InfoIcon = styled.span`
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const InfoText = styled.span`
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#6B7280')};
  line-height: 1.5;
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const TicketsSummary = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const SummaryTitle = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#6B7280')};
  margin-bottom: 12px;
`;

const TicketItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};

  &:last-child {
    border-bottom: none;
  }
`;

const TicketName = styled.span`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
`;

const TicketCount = styled.span`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#6B7280')};
`;

const InputSection = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#6B7280')};
  margin-bottom: 12px;
`;

const DeliveryOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)'};
  }
`;

const OptionRadio = styled.input`
  margin-top: 2px;
  accent-color: #fa4616;
`;

const OptionDetails = styled.div`
  flex: 1;
`;

const OptionLabel = styled.div`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  margin-bottom: 2px;
`;

const OptionDesc = styled.div`
  font-size: 12px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#6B7280')};
`;

const ModalFooter = styled.div`
  padding: 20px 24px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.02)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SecurityNote = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#6B7280')};
`;

const SecurityIcon = styled.span`
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  display: flex;
  align-items: center;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 11px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#6B7280')};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
    border-color: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.15)'};
  }
`;

const PrimaryButton = styled(Button)`
  background: #fa4616;
  color: #ffffff;
  font-weight: 600;

  &:hover:not(:disabled) {
    background: #e03e14;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(250, 70, 22, 0.3);
  }
`;

const MobileDeliveryModal = ({ isOpen, onClose, transaction, onSuccess }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [includeReceipt, setIncludeReceipt] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  const [mobileDeliveryMutation, { loading }] = useMutation(
    MOBILE_DELIVERY_MUTATION
  );

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!phoneNumber) {
      setToasterData({
        title: 'Required Field',
        message: 'Please enter a phone number',
        type: 'error',
      });
      return;
    }

    // Format phone number with country code
    const cleanedPhone = phoneNumber.replace(/\D/g, '');
    const formattedPhone = `${countryCode}${cleanedPhone}`;

    try {
      const result = await mobileDeliveryMutation({
        variables: {
          input: {
            cellphone: formattedPhone,
            calling_code: countryCode,
            purchase_id: parseInt(transaction.id, 10),
            text_receipt: includeReceipt,
          },
        },
      });

      if (result.data && result.data.mobileDelivery) {
        // Close modal immediately
        onClose();

        // Call success callback if provided
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      setToasterData({
        title: 'Error',
        message: error.message || 'Failed to send tickets. Please try again.',
        type: 'error',
      });
    }
  };

  // Calculate ticket summary grouped by ticket name
  const ticketGroups = {};

  if (transaction.users) {
    transaction.users.forEach((user) => {
      if (user.tickets) {
        user.tickets.forEach((ticket) => {
          if (!ticket.refunded) {
            const ticketName = ticket.name || 'Ticket';
            ticketGroups[ticketName] = (ticketGroups[ticketName] || 0) + 1;
          }
        });
      }
    });
  }

  const totalAmount = parseFloat(
    transaction.total?.toString().replace(/[^0-9.-]/g, '') || 0
  );

  return (
    <>
      <ModalBackdrop onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <HeaderTop>
              <ModalTitle>Mobile Ticket Delivery</ModalTitle>
              <CloseButton onClick={onClose}>×</CloseButton>
            </HeaderTop>
            <InfoBanner>
              <InfoIcon>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                </svg>
              </InfoIcon>
              <InfoText>
                This will send a link via SMS to the customer's phone. They'll
                receive a text message with a link that opens a webpage
                displaying their tickets which can be scanned at the gate. The
                delivery is instant and they'll get a confirmation once sent.
              </InfoText>
            </InfoBanner>
          </ModalHeader>

          <ModalBody>
            <TicketsSummary>
              <SummaryTitle>TICKETS TO DELIVER</SummaryTitle>
              {Object.entries(ticketGroups).map(([ticketName, count]) => (
                <TicketItem key={ticketName}>
                  <TicketName>{ticketName}</TicketName>
                  <TicketCount>× {count}</TicketCount>
                </TicketItem>
              ))}
              <TicketItem>
                <TicketName>Order #{transaction.id}</TicketName>
                <TicketCount>${totalAmount.toFixed(2)}</TicketCount>
              </TicketItem>
            </TicketsSummary>

            <InputSection>
              <PhoneInput
                label="Delivery Phone Number"
                value={phoneNumber}
                onChange={setPhoneNumber}
                countryCode={countryCode}
                onCountryChange={setCountryCode}
              />
            </InputSection>

            <InputSection>
              <SectionTitle>Additional Options</SectionTitle>
              <DeliveryOptions>
                <OptionItem onClick={() => setIncludeReceipt(false)}>
                  <OptionRadio
                    type="radio"
                    name="delivery-option"
                    checked={!includeReceipt}
                    onChange={() => setIncludeReceipt(false)}
                  />
                  <OptionDetails>
                    <OptionLabel>Tickets Only</OptionLabel>
                    <OptionDesc>
                      Send link to tickets webpage for venue entry
                    </OptionDesc>
                  </OptionDetails>
                </OptionItem>
                <OptionItem onClick={() => setIncludeReceipt(true)}>
                  <OptionRadio
                    type="radio"
                    name="delivery-option"
                    checked={includeReceipt}
                    onChange={() => setIncludeReceipt(true)}
                  />
                  <OptionDetails>
                    <OptionLabel>Tickets + Receipt</OptionLabel>
                    <OptionDesc>
                      Include purchase confirmation and receipt
                    </OptionDesc>
                  </OptionDetails>
                </OptionItem>
              </DeliveryOptions>
            </InputSection>
          </ModalBody>

          <ModalFooter>
            <SecurityNote>
              <SecurityIcon>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                </svg>
              </SecurityIcon>
              <span>Secure delivery via SMS</span>
            </SecurityNote>
            <FooterActions>
              <CancelButton onClick={onClose} disabled={loading}>
                Cancel
              </CancelButton>
              <PrimaryButton onClick={handleSend} disabled={loading}>
                {loading ? 'Sending...' : 'Send to Mobile'}
              </PrimaryButton>
            </FooterActions>
          </ModalFooter>
        </ModalContainer>
      </ModalBackdrop>

      <Toaster
        isOpen={!!toasterData}
        onClose={() => setToasterData(null)}
        title={toasterData?.title || ''}
        message={toasterData?.message || ''}
        type={toasterData?.type || 'info'}
        duration={5000}
      />
    </>
  );
};

MobileDeliveryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  transaction: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
};

export default MobileDeliveryModal;
